import { useEffect, useState } from 'react';
import { fetchUsageOverview, type UsageOverview } from '@/billing/api';
import { useNamespaceCopy } from '@/i18n/ns';
import operationCopy from '@/i18n/ns/usage-operations';
import { useIntlLocale } from '@/workspace/intl-locale';
import { useUsageCopy } from './copy';
import { formatNumber } from '@/workspace/artifacts/money';

function useOperations(overview: UsageOverview, selectedId: string | null) {
  const [loaded, setLoaded] = useState<{
    overview: UsageOverview;
    userId: string;
    operations: UsageOverview['operations'];
    failed: boolean;
  } | null>(null);
  const focus = overview.canViewCompany ? selectedId : null;
  useEffect(() => {
    if (!focus) return;
    const controller = new AbortController();
    fetchUsageOverview(
      { from: overview.from, to: overview.to, userId: focus },
      controller.signal,
    ).then(
      (result) => {
        if (!controller.signal.aborted)
          setLoaded({ overview, userId: focus, operations: result.operations, failed: false });
      },
      () => {
        if (!controller.signal.aborted)
          setLoaded({ overview, userId: focus, operations: undefined, failed: true });
      },
    );
    return () => controller.abort();
  }, [overview, focus]);
  if (!focus) return { operations: overview.operations, failed: false, loading: false };
  return loaded?.overview === overview && loaded.userId === focus
    ? { ...loaded, loading: false }
    : { operations: undefined, failed: false, loading: true };
}

export function UsageOperations({
  overview,
  selectedId,
}: {
  overview: UsageOverview;
  selectedId: string | null;
}) {
  const copy = useNamespaceCopy(operationCopy);
  const usage = useUsageCopy();
  const locale = useIntlLocale();
  const { operations, loading, failed } = useOperations(overview, selectedId);
  if (!overview.operations) return null;
  const time = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'medium' });
  return (
    <section className="cu-card" data-testid="usage-operations">
      <h2>{copy.title}</h2>
      <p className="cu-note">{copy.note}</p>
      {loading && <p role="status">{usage.loading}</p>}
      {failed && <p role="alert">{usage.unavailable}</p>}
      {operations?.hasMore && <p>{copy.more}</p>}
      <div className="ws-scroll-fade cu-table-wrap">
        <table className="cu-table cu-operations-table w-max min-w-full text-left text-sm">
          <thead className="text-ui whitespace-nowrap text-[var(--ws-muted)] [&_th]:font-medium">
            <tr>
              <th>{copy.time}</th>
              {overview.canViewCompany && <th>{usage.person}</th>}
              <th>{copy.operation}</th>
              <th>{usage.credits}</th>
            </tr>
          </thead>
          <tbody>
            {operations?.items.map((item, index) => (
              <tr key={`${item.at}-${index}`}>
                <td>
                  <time dateTime={item.at}>{time.format(new Date(item.at))}</time>
                </td>
                {overview.canViewCompany && (
                  <td>
                    {overview.people.find((person) => person.userId === item.userId)?.name ?? '—'}
                  </td>
                )}
                <td>{copy[item.kind]}</td>
                <td>
                  {item.creditMicros === null
                    ? copy.pending
                    : formatNumber(item.creditMicros / 1_000_000, locale, {
                        maximumFractionDigits: 4,
                      })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
